import React, { useState, useEffect, ReactElement, CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import { useListUserFilters, useSaveUserFilters } from '../../apis/locationTaskUserFilters';
import type { UserFilter, UserFilters } from '../../apis/locationTaskUserFilters/locationTaskUserFilter.types';
import { useDeleteCustomFilter } from '../../apis/locationTasks';
import './ManageTaskViews.scss';

const ManageTaskViews = (): ReactElement => {
	const [userFilters, setUserFilters] = useState<UserFilters>({ id: '', filters: [] });

	const dispatch: Dispatch = useDispatch();
	const history = useHistory();
	const { tasksMenuSelectedValue } = useSelector((state: any) => state.taskListState);

	const { data: loadedFilters, isFetching: isLoadingFilters } = useListUserFilters();
	const { mutate: saveFilters, isPending: isSaving } = useSaveUserFilters();
	const { mutate: deleteFilter, isPending: isDeleting } = useDeleteCustomFilter();

	const isLoading = isLoadingFilters || isSaving || isDeleting;

	useEffect(() => {
		document.title = 'Manage Task Views';
	}, []);

	// Sync local (re-orderable) state from the server whenever it loads
	useEffect(() => {
		if (loadedFilters) setUserFilters(loadedFilters);
	}, [loadedFilters]);

	const onDragEnd = (result: any) => {
		// If dropped outside any droppable area or no valid destination, do nothing
		if (!result.destination) {
			return;
		}

		const updatedFilters = [...userFilters.filters];
		// Remove the dragged filter from its original position
		const [removed] = updatedFilters.splice(result.source.index, 1);

		// Insert it in the new position
		updatedFilters.splice(result.destination.index, 0, removed);

		// Reassign ordinals
		updatedFilters.forEach((f, idx) => {
			f.ordinal = idx + 1;
		});

		const newFilters: UserFilters = {
			id: userFilters.id,
			filters: updatedFilters,
		};

		setUserFilters(newFilters);
		saveFilters(newFilters, {
			onSuccess: (response) => {
				setUserFilters((prev) => ({ ...prev, id: response.id ?? prev.id }));
				Notification('success', '', 'Filters saved');
			},
			onError: (err) => Notification('error', 'Error', err.message),
		});
	};

	const getListStyle = (isDraggingOver: boolean) => ({
		background: isDraggingOver ? '#eee' : 'white',
		borderRadius: '0.25rem',
	});

	const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
		userSelect: 'none',
		lineHeight: 1,
		margin: '0 0 2px 0',
		background: 'white',
		border: isDragging ? '2px solid #03a9f4' : '1px solid #DEE2E6',
		...draggableStyle,
	});

	const trashIconStyle = (filter: UserFilter): CSSProperties => {
		return {
			visibility: filter.isCustom ? 'visible' : 'hidden',
			cursor: 'pointer',
		};
	};

	const onDeleteFilter = (ordinal: number) => {
		const foundFilter = userFilters.filters.find((f) => f.ordinal === ordinal);
		if (!foundFilter) return;

		const filterName = foundFilter.filterName;
		// Remove from the list
		const updated = userFilters.filters.filter((f) => f.ordinal !== ordinal);
		// Re-ordinal
		updated.forEach((f, idx) => {
			f.ordinal = idx + 1;
		});

		const newFilters: UserFilters = { id: userFilters.id, filters: updated };

		setUserFilters(newFilters);
		// Save the new list, then delete the filter definition from the backend
		saveFilters(newFilters, {
			onSuccess: () => {
				deleteFilter(filterName, {
					onSuccess: () => Notification('success', '', 'Filter deleted'),
					onError: (err) => Notification('error', 'Error', err.message),
				});

				// Update the task page filter items in Redux
				const menuItems = updated.map((f) => ({ filterName: f.filterName, isCustom: f.isCustom }));
				dispatch(locationTaskActions.setTasksPageTaskMenuItems(menuItems || []) as any);

				// If the deleted filter was currently selected, reset to default
				if (filterName === tasksMenuSelectedValue) {
					dispatch(locationTaskActions.setTasksMenuSelected('') as any);
				}
			},
			onError: (err) => Notification('error', 'Error', err.message),
		});
	};

	const FilterItem = ({ filter, index }: { filter: UserFilter; index: number }) => {
		if (filter.isDefault) {
			// The default filter is not re-orderable
			return (
				<li className='list-group-item d-flex justify-content-between' style={getItemStyle(false, {})}>
					<i className='fas fa-bars me-4' style={{ visibility: 'hidden' }}></i>
					<span className='flex-grow-1'>{filter.filterName}</span>
					<i className='far fa-trash-can ms-2' style={trashIconStyle(filter)}></i>
				</li>
			);
		}

		return (
			<Draggable key={filter.ordinal} draggableId={String(filter.ordinal)} index={index}>
				{(provided, snapshot) => (
					<li
						className='list-group-item d-flex justify-content-between'
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
						<i className='fas fa-bars me-4'></i>
						<span className='flex-grow-1'>{filter.filterName}</span>
						<i className='far fa-trash-can ms-2' style={trashIconStyle(filter)} onClick={() => onDeleteFilter(filter.ordinal)}></i>
					</li>
				)}
			</Draggable>
		);
	};

	return (
		<div className='manage-task-views'>
			<LoadingIndicator isLoading={isLoading} isFullscreen>
				<div>
					<div className='mtv-breadcrumb row no-gutters'>
						<div className='col-12'>
							<a onClick={() => history.goBack()}>
								<i className='far fa-chevron-left'></i>
								<span>Go Back</span>
							</a>
						</div>
					</div>

					<div className='heading-nav row no-gutters'>
						<div className='col'>
							<div className='heading-title'>Manage Task Views</div>
						</div>
					</div>

					<div className='mtv-main row no-gutters'>
						<DragDropContext
							onDragEnd={(result) => {
								onDragEnd(result);
							}}>
							<Droppable droppableId='manageTaskViews'>
								{(provided, snapshot) => (
									<ul
										className='list-group col-sm-8'
										ref={provided.innerRef}
										{...provided.droppableProps}
										style={getListStyle(snapshot.isDraggingOver)}>
										{/* Always render the Draggables, even if userFilters.filters is empty */}
										{userFilters.filters.map((filter, index) => (
											<FilterItem filter={filter} index={index} key={filter.ordinal} />
										))}
										{provided.placeholder}
									</ul>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>
			</LoadingIndicator>
		</div>
	);
};

export default ManageTaskViews;
